import React, { useEffect } from 'react'
import { useAccountMeQuery } from '~/api/accounts'
import { preloadImage } from '~/common/domUtils'
import { links } from '~/common/links'
import { Msg, useMsg } from '~/common/localizationUtils'
import { useUniversalRouter } from '~/common/nextJsUtils'
import { TOP_NAV_HEIGHT_PX } from '~/common/scrollUtils'
import { ContactUsDrawer } from '~/components/v2/ContactUsDrawer'
import { SystemAlertBanners } from '~/components/v2/SystemAlertBanners'
import { Box, Button, chakra, Flex, FlexProps, Link, Spacer, useDisclosure, UseDisclosureReturn } from '~/design_system'
import { Menu } from '~/icons'
import { MainMenusDesktop } from '~/navigation_menu/MainMenus'
import { MainMenusMobile } from '~/navigation_menu/MainMenus.mobile'
import { ProfileMenu } from '~/navigation_menu/ProfileMenu'
import UserTasksDrawer from '~/user_tasks/UserTasksDrawer'
import { WorkdayVndlyLogo } from '~/components/v2/WorkdayVndlyLogo'
import { AssistantMenuButton } from '~/assistant/thread/AssistantMenuButton'

export function NavigationMenu(props: FlexProps) {
  const { isLoading } = useAccountMeQuery()
  const mobileMenuDisclosure = useDisclosure()

  const bgStyle = {
    bg: 'var(--vndly-color-navigation-menu-bar-background)',
    boxShadow:
      '0px 2px 8px 0px var(--color-shadow-1, rgba(31, 38, 46, 0.12)), 0px 4px 16px 0px var(--color-shadow-2, rgba(31, 38, 46, 0.08))'
  }

  return (
    <>
      <SkipToContentLink />
      <Box position="sticky" top={0} zIndex="banner">
        <Flex
          h={TOP_NAV_HEIGHT_PX}
          w="100%"
          alignItems="center"
          textStyle="bodySmall"
          {...bgStyle}
          {...props}
          data-testid="global-navigation-menu">
          {!isLoading && <OpenMobileDrawerButton onToggle={mobileMenuDisclosure.onToggle} />}
          <VndlyLogo />
          {!isLoading && <MainMenusDesktop />}
          {!isLoading && <MainMenusMobile disclosure={mobileMenuDisclosure} />}
          <Spacer />
          {!isLoading && <AssistantMenuButton />}
          <UserTasksDrawer />
          <ProfileMenu />
        </Flex>
        <SystemAlertBanners />
      </Box>
      <ContactUsDrawer />
      <SkipToMainNavigationLink />
    </>
  )
}

function OpenMobileDrawerButton({ onToggle }: { onToggle: UseDisclosureReturn['onToggle'] }) {
  const msg = useMsg()

  return (
    <chakra.button
      aria-label={msg('nav.open_mobile_menu')}
      onClick={onToggle}
      sx={{
        transition: 'background .2s linear',
        border: 'none',
        borderRight: '1px',
        borderColor: 'system.color.border.container',
        rounded: 'none',
        w: '4rem',
        h: 'full',
        _hover: { bg: 'system.color.bg.alt.default' },
        '@media (min-width: 1350px)': { display: 'none' },
        '@media print': { display: 'none' }
      }}>
      <Menu w={6} h={6} color="system.color.fg.muted.stronger" />
    </chakra.button>
  )
}

preloadImage('/static/images/logo-workday-vndly.png')

function VndlyLogo() {
  const { isCandidateRole, isLoading: isAccountsMeLoading } = useAccountMeQuery()

  let href: string
  if (isAccountsMeLoading) {
    href = '/'
  } else if (isCandidateRole) {
    href = links.candidateTimesheets.href
  } else {
    href = links.home.href
  }

  return (
    <Link
      href={href}
      data-testid="global-nav-vndly-logo"
      display="flex"
      position="relative"
      mx={1}
      borderRadius={4}
      _after={{
        content: '""',
        position: 'absolute',
        top: 2,
        left: 1,
        right: 1,
        bottom: 2,
        borderRadius: 4,
        background: 'transparent',
        mixBlendMode: 'multiply',
        transition: 'background 0.1s ease-out'
      }}
      _hover={{ _after: { background: 'system.color.bg.alt.default' } }}>
      <WorkdayVndlyLogo />
    </Link>
  )
}

function SkipToContentLink() {
  return (
    <Button
      id="skip-to-content-btn"
      onClick={e => {
        e.preventDefault()
        document.getElementById('skip-to-main-navigation-btn')?.focus()
      }}
      pointerEvents="none"
      roundedTop={0}
      position="absolute"
      left=".5rem"
      zIndex="tooltip"
      transform="translateY(-105%)"
      transition="transform 0.15s"
      opacity={0}
      bg="white"
      _focus={{
        transform: 'translateY(0%)',
        boxShadow: 'outline',
        opacity: 1
      }}>
      <Msg id="common.ui.skip_to_content" />
    </Button>
  )
}

function SkipToMainNavigationLink() {
  // Provide a skip link that takes focus on a route change within the site,
  // with a label that indicates what the link will do when activated: e.g. "skip to main navigation".
  // More information can be found here:
  // https://www.gatsbyjs.com/blog/2019-07-11-user-testing-accessible-client-routing/#recommendations-finding-common-ground
  const { events } = useUniversalRouter()

  useEffect(() => {
    const handler = () => {
      const skipToNavBtn = document.getElementById('skip-to-main-navigation-btn')
      if (skipToNavBtn) setTimeout(() => skipToNavBtn.focus(), 100)
    }
    events?.on('routeChangeComplete', handler)
    return () => events?.off('routeChangeComplete', handler)
  }, [events])

  return (
    <Button
      id="skip-to-main-navigation-btn"
      onClick={e => {
        e.preventDefault()
        document.getElementById('skip-to-content-btn')?.focus()
      }}
      pointerEvents="none"
      data-testid="skip-to-main-navigation-btn"
      position="absolute"
      left=".5rem"
      top={TOP_NAV_HEIGHT_PX}
      roundedTop={0}
      zIndex="dropdown"
      bg="white"
      transform="translateY(-110%)"
      transition="transform 0.15s"
      opacity={0}
      sx={{
        scrollMarginTop: '20px',
        '[data-using-keyboard] &:focus': {
          transform: 'translateY(0%)',
          opacity: 1,
          boxShadow: 'outline'
        },
        '@media print': { display: 'none' }
      }}>
      <Msg id="common.ui.skip_to_main_navigation" />
    </Button>
  )
}
